import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';

// ── Types ──

export interface MonthlyYearlyData {
  FUNNEL: {
    bezoekers: Record<number, number[]>;
    projecten: Record<number, number[]>;
    dossiers: Record<number, number[]>;
  };
  OMZET_GEC: Record<number, number[]>;
  OMZET_GEL: Record<number, number[]>;
  DOEL: Record<number, number[]>;
  DOSSIER_SPLIT: Record<number, {
    total: number; cons: number; b2b: number;
    cons_omzet: number; b2b_omzet: number;
  }>;
  MARGE_GEC: Record<number, { pct: number; eur: number }>;
  MARGE_GEL: Record<number, { pct: number; eur: number }>;
  YEARS: number[];
}

export interface VerkoperStats {
  omzetGec: number;
  omzetGel: number;
  margePct: number;
  projecten: number;
  dossiers: number;
  concretisatie: number;
  doel?: number;
}

export interface Verkoper {
  code: string;
  name: string;
  yearlyTotal: VerkoperStats;
  months: Record<number, VerkoperStats>;
}

export interface VerkopersData {
  verkopers: Verkoper[];
  availableYears: number[];
  availableMonths: number[];
}

export interface PostcodeItem {
  pc: string;
  plaats: string;
  lat: number;
  lng: number;
  verkopen: number;
  omzet: number;
  verkopenKeukens: number;
}

export interface GeografieData {
  postcodes: PostcodeItem[];
  storeLat: number | null;
  storeLng: number | null;
  availableYears: number[];
}

// ── Hooks ──

export function useDashboardData() {
  return useQuery({
    queryKey: ['dashboard', 'monthly-yearly'],
    queryFn: () => apiFetch<MonthlyYearlyData>('/api/dashboard/monthly-yearly'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useVerkopers(year?: number) {
  const params = year ? `?year=${year}` : '';
  return useQuery({
    queryKey: ['dashboard', 'verkopers', year],
    queryFn: () => apiFetch<VerkopersData>(`/api/dashboard/verkopers${params}`),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Multi-winkel ──

export interface WinkelKPI {
  entityId: string;
  naam: string;
  stad: string;
  omzetDezeMaand: number;
  omzetGelDezeMaand: number;
  doelDezeMaand: number;
  dossiers: { nieuw: number; actief: number; afgerond: number; total: number };
  openLeads: number;
  tevredenheid: number | null;
}

export interface MultiWinkelData {
  winkels: WinkelKPI[];
  totaal: { omzetDezeMaand: number; omzetGelDezeMaand: number; dossiers: number; openLeads: number };
  aantalWinkels: number;
  maand: number;
  jaar: number;
}

export function useMultiWinkel() {
  return useQuery({
    queryKey: ['dashboard', 'multi-winkel'],
    queryFn: () => apiFetch<MultiWinkelData>('/api/dashboard/multi-winkel'),
    staleTime: 3 * 60 * 1000,
  });
}

export function useGeografie(year?: number) {
  const params = year ? `?year=${year}` : '';
  return useQuery({
    queryKey: ['dashboard', 'geografie', year],
    queryFn: () => apiFetch<GeografieData>(`/api/dashboard/geografie${params}`),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Targets ──

export interface TargetsData {
  year: number;
  winkel: { gecreeerd: number; geleverd: number };
  verkopers: Record<string, { gecreeerd: number; geleverd: number }>;
}

export function useTargets(year?: number) {
  const params = year ? `?year=${year}` : '';
  return useQuery({
    queryKey: ['dashboard', 'targets', year],
    queryFn: () => apiFetch<TargetsData>(`/api/dashboard/targets${params}`),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveTarget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { year: number; type: string; targetBedrag: number; verkoperCode?: string }) =>
      apiFetch<{ success: boolean }>('/api/dashboard/targets', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'targets'] });
    },
  });
}

// ── Verkoper Performance ──

export interface VerkoperPerformance {
  code: string;
  naam: string;
  omzetGecreeerd: number;
  omzetGeleverd: number;
  doelGecreeerd: number;
  doelGeleverd: number;
  aantalDossiers: number;
  aantalLeads: number;
  pipelineWaarde: number;
  gewonnen: number;
  verloren: number;
  winRate: number;
  gemiddeldeDealWaarde: number;
  margePct: number;
  projecten: number;
  dossiersSmart: number;
  concretisatie: number;
}

export interface VerkoperPerformanceData {
  verkopers: VerkoperPerformance[];
  jaar: number;
  periode: string;
  winkelDoel: { gecreeerd: number; geleverd: number };
}

export function useVerkoperPerformance(jaar: number, periode: string, maand?: number) {
  const params = new URLSearchParams({ jaar: String(jaar), periode });
  if (maand) params.set('maand', String(maand));
  return useQuery({
    queryKey: ['dashboard', 'verkoper-performance', jaar, periode, maand],
    queryFn: () => apiFetch<VerkoperPerformanceData>(`/api/dashboard/verkopers/performance?${params}`),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Verkoper Follow-Up KPIs (Ixina salestraject) ──

export interface VerkoperFollowUpKpi {
  userId: string;
  code: string;
  name: string;
  overdueCount: number;
  atRiskCount: number;
  valueAtRisk: number;
  lastMeaningfulActivityDays: number | null;
}

export interface VerkoperFollowUpKpisData {
  verkopers: VerkoperFollowUpKpi[];
}

/**
 * Haal de Follow-Up Engine KPIs per verkoper op voor de Ixina HQ view.
 * Manager ziet in één oogopslag welke verkopers discipline-issues hebben.
 */
export function useVerkoperFollowUpKpis() {
  return useQuery({
    queryKey: ['dashboard', 'verkoper-followup-kpis'],
    queryFn: () => apiFetch<VerkoperFollowUpKpisData>('/api/dashboard/verkopers/followup-kpis'),
    staleTime: 60 * 1000,
  });
}

// ── Alerts ──

export interface DashboardAlertItem {
  id: string;
  type: string;
  titel: string;
  beschrijving: string;
  prioriteit: string;
  isGelezen: boolean;
  linkedEntityType: string | null;
  linkedEntityId: string | null;
  createdAt: string;
}

export interface AlertsData {
  alerts: DashboardAlertItem[];
  ongelezenCount: number;
}

export function useAlerts(ongelezen = false) {
  return useQuery({
    queryKey: ['dashboard', 'alerts', ongelezen],
    queryFn: () => apiFetch<AlertsData>(`/api/dashboard/alerts${ongelezen ? '?ongelezen=true' : ''}`),
    staleTime: 60 * 1000,
  });
}

export function useMarkAlertRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ success: boolean }>(`/api/dashboard/alerts/${id}/gelezen`, { method: 'PATCH' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'alerts'] });
    },
  });
}

export function useGenerateAlerts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<{ success: boolean; generated: number; created: number }>('/api/dashboard/alerts/generate', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'alerts'] });
    },
  });
}

// ── Dashboard Config ──

export interface WidgetConfig {
  widgetId: string;
  positie: number;
  zichtbaar: boolean;
}

export interface DashboardConfigData {
  widgetLayout: WidgetConfig[];
  defaultView: string;
}

export function useDashboardConfig() {
  return useQuery({
    queryKey: ['dashboard', 'config'],
    queryFn: () => apiFetch<DashboardConfigData>('/api/dashboard/config'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSaveDashboardConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { widgetLayout?: WidgetConfig[]; defaultView?: string }) =>
      apiFetch<{ success: boolean }>('/api/dashboard/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'config'] });
    },
  });
}

// ── Scheduled Reports ──

export interface ScheduledReportItem {
  id: string;
  naam: string;
  type: string;
  ontvangers: string[];
  inhoud: string[];
  actief: boolean;
  laatsteVerzending: string | null;
  createdBy: { firstName: string; lastName: string };
}

export function useScheduledReports() {
  return useQuery({
    queryKey: ['dashboard', 'reports'],
    queryFn: () => apiFetch<{ reports: ScheduledReportItem[] }>('/api/dashboard/reports'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { naam: string; type: string; ontvangers: string[]; inhoud: string[] }) =>
      apiFetch<{ success: boolean }>('/api/dashboard/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['dashboard', 'reports'] }); },
  });
}

export function useDeleteReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ success: boolean }>(`/api/dashboard/reports/${id}`, { method: 'DELETE' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['dashboard', 'reports'] }); },
  });
}

// ── Briefing ──

export interface BriefingData {
  datum: string;
  dagNaam: string;
  gisteren: { nieuweDossiers: number; nieuweLeads: number };
  vandaag: {
    afspraken: Array<{ id: string; titel: string; tijd: string; type: string; klant: string; dossierNummer: string | null }>;
  };
  openstaand: { alerts: number; vorderingen: number; nieuweLeads: number };
  omzet: { maandGecreeerd: number; maandDoel: number; percentage: number };
}

export function useBriefing() {
  return useQuery({
    queryKey: ['dashboard', 'briefing'],
    queryFn: () => apiFetch<BriefingData>('/api/dashboard/briefing'),
    staleTime: 10 * 60 * 1000,
  });
}

// ── Superadmin ──

export interface SuperadminData {
  year: number;
  entities: Array<{ id: string; name: string; city: string | null; omzetGec: number; omzetGel: number; bezoekers: number; dossiers: number; monthsWithData: number }>;
  totals: { omzetGec: number; omzetGel: number };
  system: { totalUsers: number; totalDossiers: number; importsLast7d: number; errorsLast7d: number; activeAlerts: number };
  recentImports: Array<{ id: string; fileName: string; status: string; dataType: string | null; createdAt: string; entity: { name: string } | null }>;
}

export function useSuperadmin() {
  return useQuery({
    queryKey: ['dashboard', 'superadmin'],
    queryFn: () => apiFetch<SuperadminData>('/api/dashboard/superadmin'),
    staleTime: 2 * 60 * 1000,
  });
}

// ── Forecast ──

export interface ForecastData {
  jaar: number;
  monthsWithData: number;
  growthFactor: number;
  forecastGec: number[];
  forecastGel: number[];
  cumulGec: number[];
  cumulGel: number[];
  projectedGec: number;
  projectedGel: number;
}

export function useForecast(jaar: number) {
  return useQuery({
    queryKey: ['dashboard', 'forecast', jaar],
    queryFn: () => apiFetch<ForecastData>(`/api/dashboard/forecast?jaar=${jaar}`),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Portfolio types & hook ──

export interface PortfolioEntity {
  id: string;
  name: string;
  type: string;
  bezoekers: number;
  projecten: number;
  dossiers: number;
  omzetGec: number;
  omzetGel: number;
  margeGecPct: number | null;
  margeGecEur: number | null;
  margeGelPct: number | null;
  margeGelEur: number | null;
}

export interface PortfolioData {
  entities: PortfolioEntity[];
  year: number;
  availableYears: number[];
}

export function usePortfolio(year?: number) {
  const params = year ? `?year=${year}` : '';
  return useQuery({
    queryKey: ['dashboard', 'portfolio', year],
    queryFn: () => apiFetch<PortfolioData>(`/api/dashboard/portfolio${params}`),
    staleTime: 5 * 60 * 1000,
  });
}

// ── Import types ──

export interface ImportRecord {
  id: string;
  fileName: string;
  fileType: string | null;
  source: string;
  status: string;
  dataType: string | null;
  rowsProcessed: number;
  rowsFailed: number;
  errors: string | null;
  createdAt: string;
  importedBy: { firstName: string; lastName: string };
}

export interface ImportStatusData {
  imports: ImportRecord[];
}

export interface UploadResult {
  success: boolean;
  type: string;
  store: string;
  records: number;
  importId: string;
}

// ── Import hooks ──

export function useSmartImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File): Promise<UploadResult> => {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('accessToken');
      const res = await fetch('/api/dashboard/smart-import/upload', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Upload mislukt: ${res.status}`);
      }

      return res.json();
    },
    onSuccess: () => {
      // Invalidate all dashboard data so it refreshes
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useImportStatus() {
  return useQuery({
    queryKey: ['dashboard', 'import-status'],
    queryFn: () => apiFetch<ImportStatusData>('/api/dashboard/smart-import/status'),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useDeleteImport() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (importId: string): Promise<{ success: boolean; message: string }> => {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`/api/dashboard/smart-import/${importId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `Verwijderen mislukt: ${res.status}`);
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}
